import {
  IonBackButton,
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonSkeletonText,
  IonTitle,
  IonToolbar,
  RefresherEventDetail,
  IonModal,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonDatetime,
  IonText,
  useIonToast,
} from '@ionic/react';
import { refresh, add } from 'ionicons/icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchStocktakings, ListStocktakingParams, fetchStockPoints } from '../../api/stocktaking';
import { StocktakingDocument } from '../../types/stocktaking';
import EnvironmentBadge from '../../components/EnvironmentBadge';
import { useHistory } from 'react-router-dom';
import getApi from '../../api';

const STATES: Array<{ value: string; label: string }> = [
  { value: 'started', label: 'Pågår' },
];

const StocktakingList: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<StocktakingDocument[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useState<ListStocktakingParams>({ limit: 50, state: 'started' });
  const history = useHistory();
  const [presentToast] = useIonToast();
  const api = getApi();
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Form state
  const [newInventoryName, setNewInventoryName] = useState('');
  const [zon, setZon] = useState('');
  const [hylla, setHylla] = useState('');
  const [fack, setFack] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchStocktakings(params);
      const list = response.results ?? response.stockTakings ?? [];
      setDocuments(list);
    } catch (err) {
      console.error('Failed to load stocktakings', err);
      setError('Kunde inte ladda inventeringar.');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);
  
  const validateForm = (): string | null => {
    if (!newInventoryName.trim()) {
      return 'Namn måste anges';
    }
    // Validate sorting logic: ZON-HYLLA-FACK-VÅNING
    if (fack && !hylla) {
      return 'Hylla måste anges om fack är ifyllt';
    }
    if (fack && !zon) {
      return 'Zon måste anges om fack är ifyllt';
    }
    if (hylla && !zon) {
      return 'Zon måste anges om hylla är ifyllt';
    }
    if (!date) {
      return 'Datum måste anges';
    }
    return null;
  };
  
  const handleCreateInventory = async () => {
    const validationError = validateForm();
    if (validationError) {
      presentToast({
        message: validationError,
        duration: 3000,
        color: 'danger',
        position: 'top'
      });
      return;
    }
    
    setCreating(true);
    try {
      const payload: any = {
        name: newInventoryName.trim(),
        stockpoint_name: 'Nytt Standardlager 2025',
        date: date
      };
      
      // Only add location fields if they have values
      if (zon.trim()) payload.zon = zon.trim();
      if (hylla.trim()) payload.hylla = hylla.trim();
      if (fack.trim()) payload.fack = fack.trim();
      
      await api.post('/api/fortnox/stocktakings/create/', payload);
      
      presentToast({
        message: 'Inventering skapad!',
        duration: 2000,
        color: 'success',
        position: 'top'
      });
      
      // Reset form
      setNewInventoryName('');
      setZon('');
      setHylla('');
      setFack('');
      setDate(new Date().toISOString().split('T')[0]);
      setShowCreateModal(false);
      
      // Reload list
      load();
    } catch (err: any) {
      console.error('Failed to create inventory', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Kunde inte skapa inventering';
      presentToast({
        message: errorMessage,
        duration: 5000,
        color: 'danger',
        position: 'top'
      });
    } finally {
      setCreating(false);
    }
  };

  const onRefresh = useCallback(
    async (event: CustomEvent<RefresherEventDetail>) => {
      try {
        await load();
      } finally {
        event.detail.complete();
      }
    },
    [load]
  );

  const filteredDocuments = useMemo(() => documents, [documents]);

  const handleStateChange = (value: string) => {
    setParams(prev => ({ ...prev, state: value || undefined }));
  };

  const handleSearch = (ev: CustomEvent) => {
    const value = (ev.detail as any)?.value as string | undefined;
    setParams(prev => ({ ...prev, page: 1, search: value } as any));
  };

  return (
    <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
      <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonBackButton defaultHref="/home" />
          </IonButtons>
          <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Inventeringar</IonTitle>
          <IonButtons slot="end" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <EnvironmentBadge />
            <IonButton onClick={() => setShowCreateModal(true)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonIcon icon={add} slot="icon-only" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonRefresher slot="fixed" onIonRefresh={onRefresh} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonRefresherContent pullingIcon={refresh} refreshingSpinner="crescent" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
        </IonRefresher>
        <IonSearchbar debounce={400} placeholder="Sök" onIonChange={handleSearch} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
        <IonSegment value={params.state ?? ''} onIonChange={ev => handleStateChange(ev.detail.value!)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          {STATES.map(option => (
            <IonSegmentButton key={option.value} value={option.value} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{option.label}</IonLabel>
            </IonSegmentButton>
          ))}
        </IonSegment>

        {error && (
          <IonItem color="danger" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{error}</IonLabel>
            <IonButton slot="end" fill="clear" onClick={load} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonIcon icon={refresh} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
            </IonButton>
          </IonItem>
        )}

        {loading ? (
          <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            {Array.from({ length: 6 }).map((_, idx) => (
              <IonItem key={idx} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <h2><IonSkeletonText animated style={{ width: '60%' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} /></h2>
                  <p><IonSkeletonText animated style={{ width: '40%' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} /></p>
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
        ) : (
          <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            {filteredDocuments.map(doc => (
              <IonItem key={doc.id} button detail onClick={() => history.push(`/stocktaking/${doc.id}`)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <h2>{doc.name || `#${doc.id}`}</h2>
                  <p>{doc.date}</p>
                </IonLabel>
                {doc.state && (
                  <IonBadge color={doc.state === 'finished' ? 'success' : doc.state === 'started' ? 'warning' : 'medium'} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    {doc.state}
                  </IonBadge>
                )}
              </IonItem>
            ))}
          </IonList>
        )}
        
        {/* Create Inventory Modal */}
        <IonModal isOpen={showCreateModal} onDidDismiss={() => setShowCreateModal(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Skapa ny inventering</IonTitle>
              <IonButtons slot="end" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonButton onClick={() => setShowCreateModal(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  Stäng
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Namn *</IonLabel>
              <IonInput
                value={newInventoryName}
                onIonChange={(e: any) => setNewInventoryName(e.detail.value || '')}
                placeholder="T.ex. Inventeringslista H1"
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </IonItem>
            
            <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                Zon
                <IonText color="medium" style={{fontSize: '0.8em', marginLeft: '4px'}} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>(Krävs om hylla/fack anges)</IonText>
              </IonLabel>
              <IonInput
                value={zon}
                onIonChange={(e: any) => {
                  const value = (e.detail.value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
                  setZon(value);
                }}
                placeholder="T.ex. H1"
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </IonItem>
            
            <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                Hylla
                <IonText color="medium" style={{fontSize: '0.8em', marginLeft: '4px'}} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>(Kräver zon)</IonText>
              </IonLabel>
              <IonInput
                value={hylla}
                onIonChange={(e: any) => {
                  const value = (e.detail.value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
                  setHylla(value);
                }}
                placeholder="T.ex. 01"
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </IonItem>
            
            <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                Fack
                <IonText color="medium" style={{fontSize: '0.8em', marginLeft: '4px'}} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>(Kräver zon + hylla)</IonText>
              </IonLabel>
              <IonInput
                value={fack}
                onIonChange={(e: any) => {
                  const value = (e.detail.value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
                  setFack(value);
                }}
                placeholder="T.ex. A"
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </IonItem>
            
            <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Datum *</IonLabel>
              <IonInput
                type="date"
                value={date}
                onIonChange={(e: any) => setDate(e.detail.value || '')}
                placeholder={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </IonItem>
            
            <div style={{marginTop: '20px'}}>
              <IonText color="medium" style={{fontSize: '0.9em'}} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <p>* = Obligatoriskt fält</p>
                <p><strong>Sorteringsregel ZON-HYLLA-FACK:</strong></p>
                <ul style={{marginLeft: '20px'}}>
                  <li>Zon kan anges ensam</li>
                  <li>Hylla kräver att zon är ifyllt</li>
                  <li>Fack kräver att både zon och hylla är ifyllda</li>
                </ul>
              </IonText>
            </div>
            
            <IonButton
              expand="block"
              onClick={handleCreateInventory}
              disabled={creating}
              style={{marginTop: '20px'}} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}            >
              {creating ? 'Skapar...' : 'Skapa inventering'}
            </IonButton>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default StocktakingList;
